import type { 
  AllocationData,
  EventInfoResponse,
  Phase2DataResponse,
  ExchangeRateResponse
} from '../types'

// 重新导出类型，让其他文件可以从这里导入
export type { EventInfoResponse, Phase2DataResponse, AllocationData, ExchangeRateResponse }

// API基础配置
const API_BASE_URL = 'https://dev-mining.api.pinai.tech';

// API错误类型
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// 重试配置
interface RetryConfig {
  maxRetries: number;
  delay: number;
  backoff: number;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  delay: 1000,
  backoff: 2,
};

// 延迟函数
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// 通用的fetch封装，支持重试
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryConfig: RetryConfig = defaultRetryConfig
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  let lastError: Error | undefined;
  
  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      
      if (!response.ok) {
        throw new ApiError(
          `API request failed: ${response.status} ${response.statusText}`,
          response.status,
          response
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      lastError = error as Error;
      
      // 如果是最后一次尝试或者是非网络错误，直接抛出
      if (attempt === retryConfig.maxRetries || error instanceof ApiError) {
        break;
      }
      
      // 等待后重试
      await delay(retryConfig.delay * Math.pow(retryConfig.backoff, attempt));
    }
  }
  
  if (lastError instanceof ApiError) {
    throw lastError;
  }
  
  throw new ApiError(
    `Network error after ${retryConfig.maxRetries + 1} attempts: ${lastError?.message || 'Unknown error'}`,
    undefined,
    lastError
  );
}

// 获取事件信息
export async function getEventInfo(address: string): Promise<EventInfoResponse> {
  if (!address) {
    throw new ApiError('Address is required');
  }

  // 验证地址格式 (基本的以太坊地址格式检查)
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    throw new ApiError('Invalid address format');
  }

  return apiRequest<EventInfoResponse>(`/binance-tge/event-info?address=${address}`);
}

// 获取 Phase2 数据
export async function getPhase2Data(address: string): Promise<Phase2DataResponse> {
  if (!address) {
    throw new ApiError('Address is required');
  }

  // 验证地址格式 (基本的以太坊地址格式检查)
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    throw new ApiError('Invalid address format');
  }

  return apiRequest<Phase2DataResponse>(`/binance-tge/phase2-data?address=${address}`);
}

// 数字格式化工具 - 添加千分位逗号
export function formatNumberWithCommas(num: number): string {
  return num.toLocaleString('en-US');
}

// 时间戳转Date对象
export function timestampToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

export async function getAllocationData(address: string): Promise<AllocationData> {
  if (!address) {
    throw new ApiError('Address is required');
  }

  // 验证地址格式 (基本的以太坊地址格式检查)
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    throw new ApiError('Invalid address format');
  }

  return apiRequest<AllocationData>(`/binance-tge/my-allocation?address=${address}`);
}

// 获取 BNB 汇率
export async function getBNBExchangeRate(amount: number = 1): Promise<ExchangeRateResponse> {
  return apiRequest<ExchangeRateResponse>(`/binance-tge/crypto-to-usd?amount=${amount}&symbol=BNB`);
} 